// Écran cuisine (KDS) : tablette murale plein écran, temps réel.
// Les envois arrivent avec un bip, gros boutons pour avancer les statuts.

import React, { useState, useEffect, useCallback } from 'react';
import { staffFetch } from '../utils/staffApi';
import {
  StaffGate, useServiceSocket, useTicker, playDing,
  ORDER_STATUS, orderWho, timeHM, minutesSince, euro
} from './staffCommon';

const LATE_MINUTES = 20;

function KitchenBoard({ staff, onLogout }) {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('all'); // all | dine_in | pickup
  const [newIds, setNewIds] = useState(new Set());
  const [clock, setClock] = useState('');
  useTicker(20000);

  const load = useCallback(async () => {
    try {
      const res = await staffFetch('/service/orders', {}, onLogout);
      if (res.ok) setOrders(await res.json());
    } catch {
      // le socket ou le prochain tick retentera
    }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  // Horloge
  useEffect(() => {
    const update = () => setClock(new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit'
    }).format(new Date()));
    update();
    const t = setInterval(update, 10000);
    return () => clearInterval(t);
  }, []);

  const connected = useServiceSocket({
    'new-order': (order) => {
      setOrders(prev => prev.some(o => o.id === order.id) ? prev : [order, ...prev]);
      setNewIds(prev => new Set(prev).add(order.id));
      setTimeout(() => setNewIds(prev => {
        const next = new Set(prev);
        next.delete(order.id);
        return next;
      }), 6000);
      playDing();
    },
    'order-updated': (order) => {
      setOrders(prev => prev.map(o => o.id === order.id ? order : o));
    }
  });

  // Resynchronisation périodique (sécurité si un événement a été manqué)
  useEffect(() => {
    const t = setInterval(load, 90000);
    return () => clearInterval(t);
  }, [load]);

  const advance = async (order) => {
    const next = ORDER_STATUS[order.status] && ORDER_STATUS[order.status].next;
    if (!next) return;
    // Optimiste : on avance tout de suite, le socket confirmera
    setOrders(prev => prev.map(o => o.id === order.id ? { ...o, status: next } : o));
    try {
      const res = await staffFetch(`/orders/${order.id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: next })
      }, onLogout);
      if (!res.ok) load();
    } catch {
      load();
    }
  };

  const active = orders
    .filter(o => ['pending', 'preparing', 'ready'].includes(o.status))
    .filter(o => filter === 'all' || o.channel === filter);

  // Urgence : à emporter triés par heure de retrait, sur place par arrivée
  const sortKey = (o) => new Date(o.channel === 'pickup' ? o.pickupTime : o.createdAt).getTime();
  const inProgress = active.filter(o => o.status !== 'ready').sort((a, b) => sortKey(a) - sortKey(b));
  const ready = active.filter(o => o.status === 'ready').sort((a, b) => sortKey(a) - sortKey(b));

  const renderCard = (o) => {
    const st = ORDER_STATUS[o.status];
    const ageMin = minutesSince(o.createdAt);
    // Sur place, la cuisine s'arrête à « Prêt » : c'est le serveur qui marque
    // « Servi » depuis sa vue de table. À emporter, « Retiré » reste ici.
    const kitchenDone = o.status === 'ready' && o.channel === 'dine_in';
    return (
      <div key={o.id} className={`kds-card ${o.status === 'ready' ? 'is-ready' : ''} ${newIds.has(o.id) ? 'is-new' : ''}`}>
        <div className="kds-card-head">
          <span className="kds-who">{orderWho(o)}</span>
          <span className={`kds-chan ${o.channel === 'dine_in' ? 'dine' : 'pickup'}`}>
            {o.channel === 'dine_in' ? 'SUR PLACE' : `🥡 ${timeHM(o.pickupTime)}`}
          </span>
          <span className={`kds-age ${ageMin >= LATE_MINUTES ? 'late' : ''}`}>{ageMin} min</span>
        </div>
        <div className="kds-items">
          {(o.items || []).map(it => (
            <div key={it.id} className={`kds-item ${it.paidWithPoints > 0 ? 'offert' : ''}`}>
              <span className="kds-qty">{it.quantity}×</span>
              <span className="kds-name">
                {it.name}
                {it.notes && <span className="kds-note">{it.notes}</span>}
              </span>
            </div>
          ))}
        </div>
        {kitchenDone ? (
          <div className="kds-await-service">🛎️ À servir — au service de jouer</div>
        ) : st.next ? (
          <button className={`kds-action to-${st.next}`} onClick={() => advance(o)}>
            {st.nextLabel}
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <div className="staff-screen">
      <div className="staff-topbar">
        <h1><span className={`staff-conn ${connected ? 'on' : ''}`} /> Cuisine</h1>
        <div className="staff-chips">
          {[['all', 'Tout'], ['dine_in', 'Sur place'], ['pickup', 'À emporter']].map(([k, l]) => (
            <button key={k} className={`staff-chip ${filter === k ? 'on' : ''}`} onClick={() => setFilter(k)}>
              {l}
            </button>
          ))}
        </div>
        <span className="staff-clock">{clock}</span>
        <span className="staff-topbar-who">{staff && staff.name}</span>
        <button className="staff-logout" onClick={onLogout}>Quitter</button>
      </div>

      {inProgress.length === 0 && ready.length === 0 && (
        <div className="kds-empty">Aucune commande en cours 🍻<br />Ça arrive, restez chauds !</div>
      )}

      {inProgress.length > 0 && (
        <>
          <div className="kds-section-title">À préparer ({inProgress.length})</div>
          <div className="kds-grid">{inProgress.map(renderCard)}</div>
        </>
      )}

      {ready.length > 0 && (
        <>
          <div className="kds-section-title">Prêt — en attente ({ready.length}) · {euro(ready.reduce((s, o) => s + parseFloat(o.total), 0))}</div>
          <div className="kds-grid">{ready.map(renderCard)}</div>
        </>
      )}
    </div>
  );
}

export default function KitchenPage() {
  return (
    <StaffGate screenName="Écran cuisine">
      {(staff, logout) => <KitchenBoard staff={staff} onLogout={logout} />}
    </StaffGate>
  );
}
